<template>
  <div class="analysis-card">
    <div class="top">
      <div class="wrapper">
        <div class="ai-icon">
          <img alt="" :src="aiLogo" />
          <div class="shadow-box"></div>
        </div>
        <span class="top-title">质量分析</span>
        <!-- <span class="top-desc">自动分析销售拜访质量，还原跟进现场</span> -->
      </div>

      <span
        v-if="mainData?.enableStaffAnalyze"
        class="top-calibration"
        @click="openCalibrationDialog"
        >人工校准</span
      >
      <div v-else-if="alreadyCalibrated" style="cursor: pointer" @click="changeAnalyzeResult">
        <i class="icon-arrow-left-right-fill t-iconfont"></i>
        <span>{{ resultText }}</span>
      </div>
    </div>
    <div class="detail-analysis__content">
      <div v-if="isInitialResults" class="calibration">
        <h3>已进行人工校准</h3>
        <div class="calibration__info">
          <div class="calibration__info-item">
            <span class="text-2">校准人</span>
            <span class="text-4">{{ mainData?.staffName }}</span>
          </div>
          <div class="calibration__info-item">
            <span class="text-2">校准时间</span>
            <span class="text-4">{{ mainData?.formattedCalibrationTime }}</span>
          </div>
        </div>
      </div>

      <div class="analysis-card__quality">
        <div class="quality">
          <span class="text-2">质量结果</span>
          <div class="quality__status">
            <div
              class="quality__status-color-block"
              :class="{
                'is-risk': showStatus == 1,
                'is-success': showStatus == 0,
                'is-gray': showStatus == 2
              }"
            ></div>
            <span class="text-4">{{ statusText }}</span>
          </div>
        </div>
        <template v-if="riskTimeList.length">
          <div v-for="item in riskTimeList" :key="item.start" class="chatlist-item">
            <div class="chatlist-item__time" @click="handleClickTime(item.start)">
              {{ formatTimeBySource(item.start) }}
            </div>
            <div class="chatlist-item__content">{{ item.content }}</div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="riskInfo.riskResult"
            style="font-family: PingFang SC; font-size: 14px; color: #323233"
            v-html="showStr"
          ></div>
        </template>
        <div v-if="!isInitialResults && suggestion" class="suggestion">
          <div class="suggestion-title">
            <div class="ai-icon">
              <img alt="boss" :src="aiLogo" />
              <div class="shadow-box"></div>
            </div>
            <span class="text">AI 建议</span>
          </div>
          <div class="suggestion-content" v-html="showSuggestion"></div>
          <div class="suggestion-references">
            <ReferenceCard
              v-for="(item, index) in evidence"
              :key="`${item.fileName}-${index}`"
              :file-name="item.fileName"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <AiCalibrationDialog
    v-if="calibrationDialogVisible"
    ref="refCalibrationDialog"
    v-model:show-detail="calibrationDialogVisible"
    :behaving-id="behavingId"
    :risk-info="riskInfo"
    @handleRefresh="handleRefresh"
  ></AiCalibrationDialog>
</template>

<script setup>
import aiLogo from '@/assets/ai-logo.png'
import { ref, onMounted, computed, watch, inject } from 'vue'
import { formatTimestamp } from '@/utils/tools'
import ReferenceCard from '@/components/reference-card/index.vue'
import AiCalibrationDialog from '@/pages/ai-board/components/ai-calibration-dialog/index.vue'

import xbb from '@xbb/xbb-utils'

defineOptions({
  name: 'AnalysisCard'
})

const emits = defineEmits(['handleRefresh', 'startWithTime'])
const props = defineProps({
  // 拜访记录id
  behavingId: {
    type: Number,
    default: 1
  },
  // 质量分析信息
  qualityAnalysisInfo: {
    type: Object,
    default: () => ({})
  },
  // 行动建议
  suggestion: {
    type: String,
    default: ''
  },
  // 云客手机类型
  ykType: {
    type: String,
    default: ''
  }
})

const showSuggestion = ref('')
const evidence = inject('evidence', ref([]))

// 是否初始分析结果
const isInitialResults = ref(false)
// 人工校准弹窗显示标记
const calibrationDialogVisible = ref(false)
// 质量结果显示状态(1：疑似风险，0: 无风险)
const showStatus = ref(props.qualityAnalysisInfo.riskStatus)
// 质量结果文本
const showStr = ref('')

// 质量分析主信息
const mainData = computed(() => {
  const res = { ...props.qualityAnalysisInfo }
  if (res.manualCalibrationTime) {
    res.formattedCalibrationTime = formatTimestamp(res.manualCalibrationTime)
  }
  return res
})

// 带有时间的分析结果
const riskTimeList = computed(() => {
  return xbb._get(props.qualityAnalysisInfo, 'riskTimeList', [])
})

// 初始分析信息
const riskInfo = computed(() => {
  return {
    riskResult: mainData.value.riskResult,
    riskStatus: mainData.value.riskStatus
  }
})

// 质量分析状态文字
const statusText = computed(() => {
  const enumText = {
    0: '无风险',
    1: '疑似风险',
    2: '无效'
  }
  return enumText[showStatus.value] || '未知'
})

// 是否已人工校准（包含人工校准的两个属性信息则表示已校准）
const alreadyCalibrated = computed(
  () => !!mainData.value?.staffRiskResult && mainData.value?.staffRiskStatus != null
)

// 校准结果操作文字
const resultText = computed(() => {
  return isInitialResults.value ? '查看初始分析结果' : '查看人工校准结果'
})

const formatTimeBySource = (val) => {
  if (['ykWechat', 'ykMobile'].includes(props.ykType)) {
    return formatTimestamp(val, 'HH:mm:ss')
  } else {
    return formatTime(val)
  }
}

/**
 * @description 格式化秒数为时分秒格式
 * @param seconds  秒数
 */
const formatTime = (seconds) => {
  const totalSeconds = Math.floor(seconds)
  const h = Math.floor(totalSeconds / 3600)
  const m = Math.floor((totalSeconds % 3600) / 60)
  const s = totalSeconds % 60

  return [
    h.toString().padStart(2, '0'),
    m.toString().padStart(2, '0'),
    s.toString().padStart(2, '0')
  ].join(':')
}

const handleClickTime = (time) => {
  emits('startWithTime', time)
}

/**
 * 初始化信息
 * @param type 质量结果使用类型（new: 使用校准结果，old: 使用初始结果）
 */
const init = async (type) => {
  if (!['old', 'new'].includes(type)) {
    console.error('无效的type参数:', type)
    return
  }

  const statusKey = type === 'old' ? 'riskStatus' : 'staffRiskStatus'
  const resultKey = type === 'old' ? 'riskResult' : 'staffRiskResult'

  showStatus.value = mainData.value[statusKey] ?? 0
  showStr.value = (mainData.value[resultKey] ?? '').replace(/\n/g, '<br/>')
}
/**
 * 刷新数据
 */
const handleRefresh = () => {
  emits('handleRefresh')
}
/**
 * 打开人工校准弹窗
 */
const openCalibrationDialog = () => {
  calibrationDialogVisible.value = true
}
/**
 * 切换分析结果
 */
const changeAnalyzeResult = () => {
  isInitialResults.value = !isInitialResults.value
}

// 监听分析结果切换
watch(
  () => isInitialResults.value,
  (val) => {
    init(val ? 'new' : 'old')
  },
  {
    immediate: true
  }
)

onMounted(() => {
  alreadyCalibrated.value && changeAnalyzeResult()
  showSuggestion.value = props.suggestion.replace(/\n/g, '<br/>')
})
</script>

<style lang="scss" scoped>
.analysis-card {
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: linear-gradient(141deg, #e9ecfa 0%, #dfeeff 100%);

  box-sizing: border-box;
  border: 1px solid #ffffff;
  box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);
  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    margin-bottom: 12px;
    .wrapper {
      display: flex;
      align-items: center;
    }
    &-calibration {
      color: #248df5;
      cursor: pointer;
    }
    .top-title {
      font-family: PingFang SC;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #323233;
    }
    .top-desc {
      margin-left: 8px;
      font-size: 14px;
      line-height: 20px;
      color: #646566;
    }
    .ai-icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 8px;

      img {
        width: 24px;
        height: 24px;
      }
      .shadow-box {
        width: 16px;
        height: 2px;
        margin-top: 4px;
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(0, 0, 0, 0.15) 0%,
          rgba(0, 0, 0, 0.02) 87%
        );
      }
    }
  }
  .content {
    padding: 16px;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
  }
}
.detail-analysis {
  flex: 1;
  overflow: hidden;
  position: relative;
  display: flex;
  padding: 12px;
  border-radius: 12px;
  /* 自动布局 */
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: linear-gradient(128deg, #e9ecfa 0%, #dfeeff 100%);
  box-sizing: border-box;
  border: 1px solid #ffffff;
  box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);
  font-family: PingFang SC;
  .top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
    .wrapper {
      display: flex;
      align-items: center;
    }
    &-calibration {
      color: #248df5;
      cursor: pointer;
    }
    .top-title {
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #323233;
    }
    .top-desc {
      margin-left: 8px;
      font-size: 14px;
      line-height: 20px;
      color: #646566;
    }
    .ai-icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 8px;

      img {
        width: 24px;
        height: 24px;
      }
      .shadow-box {
        width: 16px;
        height: 2px;
        margin-top: 4px;
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(0, 0, 0, 0.15) 0%,
          rgba(0, 0, 0, 0.02) 87%
        );
      }
    }
  }
  .detail-analysis__content {
    margin-top: 12px;
    display: flex;
    flex: 1;
    flex-direction: column;
    padding: 12px;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0px 2px 30px 2px rgba(0, 0, 0, 0.05);
    overflow: auto;
    .content-title {
      display: flex;
      flex: 1;
      align-items: center;
      cursor: pointer;
      .icon-checkbox-circle-fill {
        margin-right: 8px;
        font-size: 16px;
        color: #969799;
      }
      .desc {
        flex: 1;
        font-size: 14px;
        line-height: 20px;
        color: #646566;
      }
    }
    .content-box {
      font-family: PingFang SC;
      font-size: 14px;
      color: #323233;
    }
  }
}
.icon-arrow-left-right-fill {
  margin-right: 10px;
}
.text-2 {
  font-family: PingFang SC;
  font-size: 14px;
  color: #969799;
}
.text-4 {
  font-family: PingFang SC;
  font-size: 14px;
  color: #323233;
}

.calibration {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  margin-bottom: 16px;
  gap: 4px;
  align-self: stretch;
  width: 100%;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f7f8fa;
  z-index: 0;

  h3 {
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0em;
    color: #323233;
  }

  &__info {
    display: flex;
    flex-direction: column;
    align-items: baseline;

    &-item {
      display: flex;
      gap: 12px;
    }
  }
}
.analysis-card__quality {
  background: #ffffff;
  padding: 12px;
  border-radius: 8px;
}
.quality {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;

  &__status {
    display: flex;
    align-items: center;
    gap: 8px;

    &-color-block {
      width: 12px;
      height: 12px;
      border-radius: 4px;
    }
    .is-risk {
      background: #fd5555;
    }
    .is-success {
      background: #0fbb54;
    }
    .is-gray {
      background: #c9cdcd;
    }
  }
}
.chatlist-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  .chatlist-item__time {
    width: 66px;
    border-radius: 4px 24px 24px 4px;
    background: #faf3ff;
    padding: 2px 8px;
    height: 20px;
    font-size: 12px;
    line-height: 16px;
    color: #7a52f4;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
  }
  .chatlist-item__content {
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
  }
}
.suggestion {
  margin-top: 16px;
  padding: 12px;
  border-radius: 8px;
  background: #f7f8fa;
  box-sizing: border-box;
  border: 1px solid #e5e6eb;
  display: flex;
  flex-direction: column;
  .suggestion-title {
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    .ai-icon {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-right: 8px;

      img {
        width: 20px;
        height: 20px;
      }
      .shadow-box {
        width: 16px;
        height: 2px;
        margin-top: 4px;
        background: radial-gradient(
          50% 50% at 50% 50%,
          rgba(0, 0, 0, 0.15) 0%,
          rgba(0, 0, 0, 0.02) 87%
        );
      }
    }
    .text {
      // margin-left: 8px;
      font-family: PingFang SC;
      font-size: 14px;
      font-weight: 600;
      line-height: 20px;
      color: #323233;
    }
  }
  .suggestion-content {
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #323233;
  }
  .suggestion-references {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
  }
}
</style>
